import React, { useMemo, useState } from 'react';
import type { AuthorIntakeItem, IntakeFilterKind, IntakeStats } from '../types';
import '../author-intake.css';

interface Props {
  items: AuthorIntakeItem[];
  stats: IntakeStats;
  currentFilter: IntakeFilterKind;
  onFilterChange: (kind: IntakeFilterKind) => void;
  onViewIntake: (item: AuthorIntakeItem) => void;
  onOpenDiscussion: (item: AuthorIntakeItem) => void;
  onBack: () => void;
  onClose: () => void;
}

const KIND_META: Record<string, { label: string; color: string; icon: string }> = {
  SERVICE: { label: 'Услуга', color: '#41d2ff', icon: '✦' },
  READY_ARTICLE: { label: 'Готовая статья', color: '#49ddb7', icon: '✎' },
  AUTHOR_DATA: { label: 'Данные автора', color: '#e2ad3a', icon: '◆' },
  COAUTHOR_SEARCH: { label: 'Поиск соавторов', color: '#9e80ff', icon: '◎' },
};

/** Author intake inbox — styles: author-intake.css */
export const AuthorIntakeView: React.FC<Props> = ({
  items,
  stats,
  currentFilter,
  onFilterChange,
  onViewIntake,
  onOpenDiscussion,
  onBack,
  onClose,
}) => {
  const [search, setSearch] = useState('');

  const s = stats || {
    total: items.length,
    services: 0,
    ready_articles: 0,
    author_data: 0,
    coauthor_search: 0,
    on_moderation: 0,
  };

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (currentFilter !== 'ALL') {
        const map: Record<string, string> = {
          SERVICE: 'SERVICE',
          READY_ARTICLE: 'READY_ARTICLE',
          AUTHOR_DATA: 'AUTHOR_DATA',
          COAUTHOR_SEARCH: 'COAUTHOR_SEARCH',
        };
        if (item.kind !== map[currentFilter] && item.kind !== currentFilter) return false;
      }
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        item.author_name.toLowerCase().includes(q) ||
        item.author_email.toLowerCase().includes(q) ||
        item.author_id_code.toLowerCase().includes(q) ||
        item.subject_title.toLowerCase().includes(q)
      );
    });
  }, [items, currentFilter, search]);

  const statusRu = (status: string) =>
    (
      {
        NEW: 'Новое',
        IN_PROGRESS: 'В работе',
        NEED_INFO: 'Нужны сведения',
        APPROVED: 'Одобрено',
        DONE: 'Завершено',
        REJECTED: 'Отклонено',
        PENDING: 'На модерации',
      } as Record<string, string>
    )[status] ?? status;

  return (
    <div className="author-intake-overlay open" role="dialog" aria-modal="true" aria-label="Авторские обращения">
      <div className="author-intake-shell">
        <header className="author-intake-top">
          <button type="button" onClick={onBack}>
            ← Назад
          </button>
          <div className="copy">
            <h1>Авторские обращения</h1>
            <p>Рабочий реестр владельца</p>
          </div>
          <div className="spacer" />
          <button type="button" onClick={onClose}>
            Закрыть ×
          </button>
        </header>

        <main className="author-intake-content">
          <section className="author-intake-hero">
            <div>
              <small>Владелец системы · приём обращений</small>
              <h2>Авторские обращения</h2>
              <p>
                Рабочая очередь: услуги, участие в готовых статьях, данные авторов и отдельная
                модерация объявлений о поиске соавторов.
              </p>
            </div>
            <aside className="author-intake-identity">
              <strong>Владелец системы</strong>
              <span>Научный CRM Zertteu Academy</span>
              <b>Только владелец</b>
            </aside>
          </section>

          <div className="author-intake-ownerstats">
            <div className="author-intake-stat">
              <small>Всего</small>
              <b>{s.total}</b>
            </div>
            <div className="author-intake-stat">
              <small>Услуги</small>
              <b>{s.services}</b>
            </div>
            <div className="author-intake-stat">
              <small>Готовые статьи</small>
              <b>{s.ready_articles}</b>
            </div>
            <div className="author-intake-stat">
              <small>На модерации</small>
              <b>{s.on_moderation}</b>
            </div>
          </div>

          <div className="author-intake-tabs">
            {(
              [
                ['ALL', 'Все', s.total],
                ['SERVICE', 'Услуги', s.services],
                ['READY_ARTICLE', 'Готовые статьи', s.ready_articles],
                ['AUTHOR_DATA', 'Данные автора', s.author_data],
                ['COAUTHOR_SEARCH', 'Поиск соавторов', s.coauthor_search],
              ] as const
            ).map(([id, label, count]) => (
              <button
                key={id}
                type="button"
                className={currentFilter === id ? 'active' : ''}
                onClick={() => onFilterChange(id)}
              >
                {label} · {count}
              </button>
            ))}
          </div>

          <div style={{ marginBottom: 14 }}>
            <input
              className="author-intake-field"
              style={{
                width: '100%',
                minHeight: 42,
                padding: '0 12px',
                borderRadius: 9,
                border: '1px solid rgba(255,255,255,.14)',
                background: '#071c29',
                color: '#edf8fc',
              }}
              placeholder="Поиск по автору, email, Academia ID или теме…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="author-intake-inbox">
            {filteredItems.map((item) => {
              const meta = KIND_META[item.kind] || {
                label: item.kind,
                color: '#41d2ff',
                icon: '•',
              };
              return (
                <article
                  key={item.id}
                  className="author-intake-inbox-item"
                  style={{ ['--accent' as string]: meta.color }}
                >
                  <div className="ico">{meta.icon}</div>
                  <div>
                    <h4>
                      {meta.label} · {item.author_name}
                    </h4>
                    <p>
                      {item.author_email} · ID {item.author_id_code}
                    </p>
                    <time>
                      {new Date(item.created_at).toLocaleString('ru-RU')} · {statusRu(item.status)}
                      {item.subject_title ? ` · ${item.subject_title}` : ''}
                    </time>
                  </div>
                  <div className="right au124-owner-actions">
                    <button type="button" className="author-intake-btn" onClick={() => onViewIntake(item)}>
                      Посмотреть
                    </button>
                    <button
                      type="button"
                      className="author-intake-btn"
                      onClick={() => onOpenDiscussion(item)}
                    >
                      Написать
                    </button>
                  </div>
                </article>
              );
            })}
            {!filteredItems.length ? (
              <div className="author-intake-empty">Пока нет обращений этого типа.</div>
            ) : null}
          </div>
        </main>
      </div>
    </div>
  );
};
